(() => {
  'use strict';
  const cache = new WeakMap();
  const suppressed = new WeakMap();
  let gesture;
  const prepared = new Set();
  const galleryFor = target => target.closest?.('[data-astro-card-gallery]');
  const key = src => {
    try { return new URL(src, location.href).pathname.replace(/_\d+x\d*(?=\.)/, ''); }
    catch { return src; }
  };
  function imagesFor(gallery) {
    if (!cache.has(gallery)) {
      try { cache.set(gallery, JSON.parse(gallery.querySelector('[data-astro-gallery-images]').textContent)); }
      catch { cache.set(gallery, []); }
    }
    return cache.get(gallery);
  }
  function imageSource(gallery, image) {
    if (!image.src.includes('/cdn/shop/')) return image.src;
    const url = new URL(image.src, location.href);
    const img = gallery.querySelector('.m-product-card__main-image img');
    const width = Math.min(1066, Math.max(360, Math.ceil((img?.clientWidth || 360) * Math.min(devicePixelRatio || 1, 2) / 160) * 160));
    url.searchParams.set('width', width);
    return url.href;
  }
  function currentIndex(gallery, images) {
    const img = gallery.querySelector('.m-product-card__main-image img');
    return Math.max(0, images.findIndex(image => key(image.src) === key(img?.getAttribute('src') || img?.currentSrc || '')));
  }
  function prepare(gallery, both = false) {
    const images = imagesFor(gallery);
    if (images.length < 2) return;
    const index = currentIndex(gallery, images);
    (both ? [1, -1] : [1]).forEach(direction => {
      const src = imageSource(gallery, images[(index + direction + images.length) % images.length]);
      if (prepared.has(src)) return;
      prepared.add(src);
      const preload = new Image();
      preload.decoding = 'async';
      preload.src = src;
    });
  }
  function step(gallery, direction) {
    const images = imagesFor(gallery);
    const img = gallery.querySelector('.m-product-card__main-image img');
    if (!img || images.length < 2) return;
    const current = currentIndex(gallery, images);
    const next = images[(current + direction + images.length) % images.length];
    const src = imageSource(gallery, next);
    const responsive = img.closest('responsive-image');
    if (responsive) responsive.src = src;
    img.removeAttribute('srcset');
    img.src = src;
    img.alt = next.alt || '';
    prepare(gallery);
  }
  document.addEventListener('click', event => {
    const gallery = galleryFor(event.target);
    if (!gallery) return;
    const arrow = event.target.closest('[data-astro-gallery-step]');
    if (arrow) {
      event.preventDefault();
      event.stopImmediatePropagation();
      step(gallery, Number(arrow.dataset.astroGalleryStep));
    } else if ((suppressed.get(gallery) || 0) > performance.now() && event.target.closest('.m-product-card__link')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('touchstart', event => {
    gesture = null;
    if (event.touches.length !== 1) return;
    const gallery = galleryFor(event.target);
    if (!gallery || event.target.closest('button, .m-product-card__action') || imagesFor(gallery).length < 2) return;
    prepare(gallery, true);
    const touch = event.touches[0];
    gesture = { gallery, id: touch.identifier, x: touch.clientX, y: touch.clientY, horizontal: false, advanced: false };
  }, { capture: true, passive: true });
  document.addEventListener('touchmove', event => {
    if (!gesture) return;
    if (event.touches.length !== 1) { gesture = null; return; }
    const touch = event.touches[0];
    const dx = touch.clientX - gesture.x;
    const dy = touch.clientY - gesture.y;
    if (!gesture.horizontal && Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { gesture = null; return; }
    if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.15) gesture.horizontal = true;
    if (gesture.horizontal) {
      if (!gesture.advanced && Math.abs(dx) >= 18) {
        step(gesture.gallery, dx < 0 ? 1 : -1);
        gesture.advanced = true;
        suppressed.set(gesture.gallery, performance.now() + 500);
      }
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, { capture: true, passive: false });
  document.addEventListener('touchend', event => {
    if (!gesture) return;
    const current = gesture;
    gesture = null;
    const touch = [...event.changedTouches].find(touch => touch.identifier === current.id);
    if (!touch) return;
    const dx = touch.clientX - current.x;
    const dy = touch.clientY - current.y;
    if (current.advanced || (Math.abs(dx) >= 18 && Math.abs(dx) > Math.abs(dy) * 1.15)) {
      if (!current.advanced) step(current.gallery, dx < 0 ? 1 : -1);
      suppressed.set(current.gallery, performance.now() + 500);
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, { capture: true, passive: false });
  document.addEventListener('touchcancel', () => { gesture = null; }, true);
  document.addEventListener('pointerover', event => {
    if (event.pointerType !== 'mouse') return;
    const gallery = galleryFor(event.target);
    if (gallery && !gallery.contains(event.relatedTarget)) prepare(gallery, true);
  }, true);
  function init() {
    if (!('IntersectionObserver' in window)) return;
    const watched = new WeakSet();
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      prepare(entry.target);
      observer.unobserve(entry.target);
    }));
    function watch(root) {
      const galleries = [...root.querySelectorAll('[data-astro-card-gallery]')];
      if (root.matches?.('[data-astro-card-gallery]')) galleries.push(root);
      galleries.forEach(gallery => {
        if (watched.has(gallery)) return;
        watched.add(gallery);
        observer.observe(gallery);
      });
    }
    watch(document);
    new MutationObserver(changes => changes.forEach(change => [...change.addedNodes].forEach(node => {
      if (node.nodeType === 1) watch(node);
    }))).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
